import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Send, UserPlus, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { CompBadge } from "@/components/OpportunityCard";

// Lets a business invite one creator to one of their active opportunities.
export function InviteToOpportunity({ creatorUserId, creatorName, trigger }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/opportunities/mine")
      .then((r) => {
        const active = r.data.opportunities.filter((o) => o.status !== "closed");
        setOpps(active);
        if (active.length) setSelected(active[0].id);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const send = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${selected}/invite`, { creator_user_ids: [creatorUserId] });
      if (r.data.invited > 0) toast.success(`${creatorName} invited 🎯`);
      else toast.info(`${creatorName} has already been invited or applied.`);
      setOpen(false);
    } catch { toast.error("Could not send invitation"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="invite-to-opp-btn" variant="outline" className="rounded-full">
            <UserPlus className="mr-2 h-4 w-4" /> Invite to Opportunity
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="invite-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite {creatorName}</DialogTitle>
          <DialogDescription>Choose one of your opportunities to invite this creator to. They'll get a direct invitation.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : opps.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-300 p-5 text-center">
            <Megaphone className="mx-auto h-6 w-6 text-zinc-300" />
            <p className="mt-2 text-sm text-zinc-600">You have no active opportunities yet.</p>
            <Button data-testid="invite-create-opp" onClick={() => navigate("/business/opportunities/create")} className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700">Create an opportunity</Button>
          </div>
        ) : (
          <>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {opps.map((o) => (
                <button key={o.id} data-testid={`invite-opp-option-${o.id}`} type="button" onClick={() => setSelected(o.id)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${selected === o.id ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                  <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected === o.id ? "border-emerald-600" : "border-zinc-300"}`}>
                    {selected === o.id && <div className="h-2 w-2 rounded-full bg-emerald-600" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-zinc-900">{o.title || "Untitled"}</p>
                    <p className="truncate text-xs text-zinc-500">{o.location} · {o.status}</p>
                  </div>
                  <CompBadge type={o.compensation_type} />
                </button>
              ))}
            </div>
            <Button data-testid="send-invitation" onClick={send} disabled={busy || !selected} className="h-11 w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Send invitation</>}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
